import { useMutation, useQuery } from '@tanstack/react-query';

import {
  createCompliance,
  deleteCompliance,
  getPost,
  getPosts,
  rescrapePosts,
  updateCompliance,
  updateYTPosts
} from '@/api/posts';
import { UnknownObject } from '@/models/misc';
import { Posts } from '@/models/posts';

import { Callback } from '.';

type PostsProps = {
  params: Posts.ListParams;
  onError?: (_error: unknown) => void;
  enabled?: boolean;
  cacheTime?: number;
};

export const useFetchPosts = ({ params, enabled, cacheTime }: PostsProps) =>
  useQuery({
    queryKey: ['posts', params],
    queryFn: async () => {
      const result = await getPosts(params);
      return result.data;
    },
    placeholderData: (previousData) => previousData,
    enabled,
    gcTime: cacheTime
  });

export const useFetchPost = ({
  id,
  params,
  onSuccess,
  onError,
  enabled
}: {
  id: number;
  params?: Posts.DetailParams;
  onSuccess?: (_data: Posts.Details) => void;
  onError?: (_error: unknown) => void;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: ['post', id, params],
    queryFn: async () => {
      const { data } = await getPost(id, params ?? {});
      return data;
    },
    enabled,
    meta: { onSuccess, onError }
  });

export const useCreateCompliance = (
  onSuccess: (data: Posts.Compliance) => void,
  onError: () => void
) =>
  useMutation({
    mutationKey: ['createCompliance'],
    mutationFn: async (payload: UnknownObject) => {
      const response = await createCompliance(payload);
      return response.data;
    },
    onSuccess,
    onError
  });

export const useRescrapePosts = (
  id: number,
  onSuccess: () => void,
  onError: (_error: UnknownObject) => void
) =>
  useMutation({
    mutationKey: ['posts', id],
    mutationFn: async () => {
      await rescrapePosts(id);
    },
    onSuccess,
    onError
  });

export const useUpdateYTPosts = (
  id: number,
  onSuccess: () => void,
  onError: (_error: UnknownObject) => void
) =>
  useMutation({
    mutationKey: ['posts', id],
    mutationFn: async () => {
      await updateYTPosts(id);
    },
    onSuccess,
    onError
  });

export const useUpdateCompliance = (
  onSuccess: (data: Posts.Compliance) => void,
  onError: () => void
) =>
  useMutation({
    mutationKey: ['updateCompliance'],
    mutationFn: async (payload: UnknownObject) => {
      const response = await updateCompliance(payload);
      return response.data;
    },
    onSuccess,
    onError
  });

export const useDeleteCompliance = (
  onSuccess: Callback,
  onError: Callback,
  onSettled: Callback
) =>
  useMutation({
    mutationKey: ['deleteCompliance'],
    mutationFn: deleteCompliance,
    onSuccess,
    onError,
    onSettled
  });
